import { memo, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { formatCurrency, formatDateParam } from "helpers";
import { MarginStatsResponse } from "services/api/dashboard/types";
import { Routes as FinanceRoutes } from "ui/modules/finance/navigation";
import DashboardTooltip, { TooltipDivider, TooltipRow, TooltipTitle } from "../../../../common/DashboardTooltip";
import BarTooltip, { BarTooltipDivider, BarTooltipRow, BarTooltipTitle } from "../../../../common/HoverPopup";
import CardHeader, { DeltaBadge } from "../../../../common/CardHeader";
import { text } from "../../../../common/dashboardPalette";
import AxisLabels from "../../../../common/AxisLabels";

interface VehicleMarginCardProps {
  data: MarginStatsResponse;
}

/**
 * Mjesečna marža + indirektno prihodi (prodajna cijena) i rashodi (nabavna cijena).
 * Visina svake mjesečne kolone je proporcionalna ukupnoj prodajnoj cijeni — unutar nje:
 *  - svjetlo siv segment dolje = nabavna (rashod)
 *  - zeleni segment gore = marža (prihod − rashod)
 * Tako se omjer marže prema prihodu vidi na prvi pogled.
 */
const VehicleMarginCard: React.FC<VehicleMarginCardProps> = ({ data }) => {
  const navigate = useNavigate();
  const months = data?.months ?? [];
  const totalMargin = data?.totalMargin ?? 0;
  const changePercent = data?.changePercent ?? 0;

  // Klik na mjesec -> pretraga troškova za taj bucket (period marže). `end` je ekskluzivan pa ga za
  // inkluzivni "Datum do" pomičemo za 1 ms unatrag.
  const handleMonthClick = (start: string | null, end: string | null) => {
    const params = new URLSearchParams({ tab: "search" });
    if (start) params.set("expenseDateFrom", formatDateParam(new Date(start))!);
    if (end) params.set("expenseDateTo", formatDateParam(new Date(new Date(end).getTime() - 1))!);
    navigate(`${FinanceRoutes.ExpenseSearchPageRouter}?${params.toString()}`);
  };

  const totals = useMemo(() => {
    const selling = months.reduce((s, m) => s + (m.totalSellingPrice ?? 0), 0);
    const purchase = months.reduce((s, m) => s + (m.totalPurchasePrice ?? 0), 0);
    return { selling, purchase };
  }, [months]);

  // Skala: visina bara = % od najveće prodajne cijene (uključuje cijeli stupac).
  const maxSelling = useMemo(
    () => Math.max(...months.map((m) => m.totalSellingPrice ?? 0), 1),
    [months]
  );

  if (months.length === 0) {
    return (
      <div className="rounded-sm px-4 py-5 bg-white h-full flex items-center justify-center">
        <span className="text-sm text-slate-400">Nema podataka</span>
      </div>
    );
  }

  return (
    <div className="rounded-sm px-4 py-5 bg-white">
      <CardHeader
        title="Ukupna marža"
        subtitle={
          <>
            Po mjesecima · {formatCurrency.format(totals.selling)} prihoda /
            {" "}{formatCurrency.format(totals.purchase)} rashoda
          </>
        }
        className="mb-10"
        right={
          <DashboardTooltip
            trigger={
              <div className="flex flex-col items-end cursor-help gap-1">
                <span className={`text-2xl font-bold ${text.primary}`}>
                  {formatCurrency.format(totalMargin)}
                </span>
                {changePercent !== 0 && <DeltaBadge value={changePercent} />}
              </div>
            }
          >
            <TooltipTitle>Pregled marže</TooltipTitle>
            <TooltipRow label="Prihodi (prodajna)" value={formatCurrency.format(totals.selling)} />
            <TooltipRow label="Rashodi (nabavna)" value={formatCurrency.format(totals.purchase)} />
            <TooltipDivider />
            <TooltipRow label="Marža" value={formatCurrency.format(totalMargin)} emphasis={totalMargin >= 0 ? "positive" : "negative"} />
          </DashboardTooltip>
        }
      />

      <div className="w-full mb-2">
        {/* Marža labeli iznad parova barova */}
        <div className="flex justify-between gap-1 sm:gap-2 mb-1 h-[18px]">
          {months.map((m) => {
            const margin = m.totalMargin ?? 0;
            const isPos = margin >= 0;
            return (
              <div key={`${m.month}-label`} className="flex-1 flex items-end justify-center">
                {margin !== 0 && (
                  <span
                    className={`text-xs font-medium px-1 rounded ${
                      isPos ? "text-emerald-700" : "text-rose-700"
                    }`}
                  >
                    {isPos ? "+" : ""}{formatCurrency.format(margin)}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex justify-between gap-1 sm:gap-2 mb-2 h-[clamp(160px,26vh,280px)]">
          {months.map((m) => {
            const sellingTotal = m.totalSellingPrice ?? 0;
            const purchaseTotal = m.totalPurchasePrice ?? 0;
            const margin = m.totalMargin ?? 0;

            const sellingHeight = (sellingTotal / maxSelling) * 100;
            const purchaseHeight = (purchaseTotal / maxSelling) * 100;

            // Prozirni gradient (jak na vrhu -> blijedi prema dnu) + 2px border na vrhu, kao ostali grafovi.
            // Tekući mjesec zasićeniji. Pune klase u stringovima — Tailwind JIT.
            const sellingBar = m.isCurrent
              ? "bg-gradient-to-b from-emerald-500/90 to-emerald-500/40 border-t-2 border-emerald-500 group-hover/bar:from-emerald-500 group-hover/bar:to-emerald-500/50"
              : "bg-gradient-to-b from-emerald-500/70 to-emerald-500/30 border-t-2 border-emerald-500 group-hover/bar:from-emerald-500/80 group-hover/bar:to-emerald-500/40";
            const purchaseBar = m.isCurrent
              ? "bg-gradient-to-b from-rose-500/90 to-rose-500/40 border-t-2 border-rose-500 group-hover/bar:from-rose-500 group-hover/bar:to-rose-500/50"
              : "bg-gradient-to-b from-rose-500/70 to-rose-500/30 border-t-2 border-rose-500 group-hover/bar:from-rose-500/80 group-hover/bar:to-rose-500/40";

            return (
              <div
                key={m.month}
                className="flex flex-col items-center flex-1 h-full group/bar relative cursor-pointer"
                onClick={() => handleMonthClick(m.start, m.end)}
              >
                <div className="w-full flex-1 flex items-end justify-center gap-0.5">
                  <div
                    className={`flex-1 rounded-sm origin-bottom animate-growUp transition-all ${sellingBar}`}
                    style={{ height: `${Math.max(sellingHeight, 5)}%` }}
                  />
                  <div
                    className={`flex-1 rounded-sm origin-bottom animate-growUp transition-all ${purchaseBar}`}
                    style={{ height: `${Math.max(purchaseHeight, 5)}%` }}
                  />
                </div>
                <BarTooltip position="top-far">
                  <BarTooltipTitle>
                    {m.monthLabel} · {m.count} {m.count === 1 ? "vozilo" : "vozila"}
                  </BarTooltipTitle>
                  <BarTooltipRow label="Prihodi" value={formatCurrency.format(sellingTotal)} emphasis="positive" />
                  <BarTooltipRow label="Rashodi" value={formatCurrency.format(purchaseTotal)} emphasis="negative" />
                  <BarTooltipDivider />
                  <BarTooltipRow
                    label="Marža"
                    value={formatCurrency.format(margin)}
                    emphasis={margin >= 0 ? "positive" : "negative"}
                  />
                </BarTooltip>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between gap-1 sm:gap-2">
          <AxisLabels labels={months.map((m) => m.monthLabel)} className={`text-xs sm:text-sm ${text.subtle}`} />
        </div>
      </div>

      <div className="flex items-center justify-center gap-4 mt-3">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />
          <span className={`text-sm ${text.muted}`}>Prihodi</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-rose-500" />
          <span className={`text-sm ${text.muted}`}>Rashodi</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-emerald-700 font-medium">+€</span>
          <span className={`text-sm ${text.muted}`}>Marža (iznad para)</span>
        </div>
      </div>
    </div>
  );
};

export default memo(VehicleMarginCard);
